import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { ENDPOINTS } from 'src/app/_configs/endpoints';
import { PaymentStatusData } from '../_components/payment-status-modal/payment-status-modal.component';
import { environment } from 'src/environments/environment';
import { tap } from 'rxjs/operators';

/** A cached active-plan response, stamped so it can be aged out. */
interface ActivePlanCacheEntry {
    cachedAt: number;
    data: any;
}

/** The answer to "is this coupon any good on this order?" */
export interface CouponQuote {
    valid: boolean;
    code: string;
    discountType?: string;
    discountValue?: number;
    discount: number;
    amount: number;
    payable: number;
    message?: string;
}

export interface PaymentInitiateRequest {
    userId: string;
    gstin?: string;
    txnId: string;
    amount: string;
    firstName: string;
    email: string;
    phone: string;
    productInfo: string;
    /** Optional discount code; the server decides what it is worth. */
    couponCode?: string;
    planType?: string;
}

export interface PaymentInitiateResponse {
    payuBaseUrl: string;
    key: string;
    txnid: string;
    amount: string;
    firstname: string;
    email: string;
    phone: string;
    productinfo: string;
    surl: string;
    furl: string;
    udf1: string;
    udf2: string;
    udf3: string;
    udf4: string;
    udf5: string;
    hash: string;
}

@Injectable({
    providedIn: 'root'
})
export class PaymentService {
    private baseUrl = environment.TENDER.API_BASE_URL;
    private readonly PAYMENT_STATUS_STORAGE_KEY = 'pendingPaymentStatus';

    /**
     * How long a cached active-plan response stays usable.
     *
     * Without a TTL the entry lived for the whole tab session, so a plan
     * changed on the backend — an expiry extended by support, say — stayed
     * invisible until the user closed the tab or logged out. Ten minutes keeps
     * the call off the hot path while letting any backend change surface on its
     * own.
     */
    private readonly ACTIVE_PLAN_CACHE_TTL_MS = 10 * 60 * 1000;

    constructor(private http: HttpClient) { }

    private isBrowser(): boolean {
        return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
    }

    private isSessionStorageAvailable(): boolean {
        try {
            if (typeof window === 'undefined' || !window.sessionStorage) {
                return false;
            }
            const testKey = '__test__';
            window.sessionStorage.setItem(testKey, '1');
            window.sessionStorage.removeItem(testKey);
            return true;
        } catch (e) {
            return false;
        }
    }

    clearActivePlanDetailsCache(userId: string): void {
        if (this.isBrowser() && this.isSessionStorageAvailable() && userId) {
            const cacheKey = `activePlanDetails_${userId}`;
            sessionStorage.removeItem(cacheKey);
        }
    }

    updateActivePlanDetailsCache(userId: string, data: any): void {
        if (this.isBrowser() && this.isSessionStorageAvailable() && userId) {
            const cacheKey = `activePlanDetails_${userId}`;
            const entry: ActivePlanCacheEntry = { cachedAt: Date.now(), data };
            sessionStorage.setItem(cacheKey, JSON.stringify(entry));
        }
    }

    /**
     * The cached plan response, or null when there is none, it is unreadable,
     * or it has aged past the TTL. Entries written before the TTL existed have
     * no cachedAt and are treated as stale so they get refetched once.
     */
    private readActivePlanDetailsCache(userId: string): any | null {
        if (!this.isBrowser() || !this.isSessionStorageAvailable() || !userId) {
            return null;
        }

        const cacheKey = `activePlanDetails_${userId}`;
        const raw = sessionStorage.getItem(cacheKey);
        if (!raw) {
            return null;
        }

        try {
            const entry = JSON.parse(raw) as ActivePlanCacheEntry;
            const cachedAt = entry?.cachedAt;
            if (typeof cachedAt !== 'number' || Date.now() - cachedAt >= this.ACTIVE_PLAN_CACHE_TTL_MS) {
                this.clearActivePlanDetailsCache(userId);
                return null;
            }
            return entry.data;
        } catch (e) {
            this.clearActivePlanDetailsCache(userId);
            return null;
        }
    }


    fetchActivePlanDetailsByUserId(userId: string, forceRefresh: boolean = false): Observable<any> {
        if (!forceRefresh) {
            const cached = this.readActivePlanDetailsCache(userId);
            if (cached) {
                return of(cached);
            }
        }
        const url = `${this.baseUrl}${ENDPOINTS.TENDER.PAYMENTS.ACTIVE_PLAN_DETAILS}/${userId}`;
        return this.http.get<any>(url).pipe(
            tap(response => {
                if (this.isBrowser() && this.isSessionStorageAvailable()) {
                    this.updateActivePlanDetailsCache(userId, response);
                }
            })
        );
    }

    getPaymentInfoByTxnId(txnId: string, userId?: string): Observable<PaymentInitiateResponse> {
        // If userId is provided, clear the active plan cache for this user
        this.clearActivePlanDetailsCache(userId || '');
        const url = `${this.baseUrl}${ENDPOINTS.TENDER.PAYMENTS.INFO}/${txnId}`;
        return this.http.get<PaymentInitiateResponse>(url);
    }

    initiatePayment(paymentData: PaymentInitiateRequest): Observable<PaymentInitiateResponse> {
        const url = `${this.baseUrl}${ENDPOINTS.TENDER.PAYMENTS.INITIATE}`;
        return this.http.post<PaymentInitiateResponse>(url, paymentData);
    }

    //TODO Drive this via backend
    generateTransactionId(): string {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 1000);
        return `TXN_${timestamp}${random}`;
    }

    /** The prepared PayU form behind a link our team sent. No sign-in needed. */
    openPaymentLink(token: string): Observable<PaymentInitiateResponse> {
        return this.http.get<PaymentInitiateResponse>(
            `${this.baseUrl}${ENDPOINTS.TENDER.PAYMENTS.LINK}/${token}`);
    }

    /** What a coupon is worth on this order; the server has the last word. */
    validateCoupon(code: string, planType: string, amount: string, userId: string): Observable<CouponQuote> {
        return this.http.post<CouponQuote>(
            `${this.baseUrl}${ENDPOINTS.TENDER.PAYMENTS.VALIDATE_COUPON}`,
            { code, planType, amount, userId });
    }

    submitToPayU(paymentResponse: PaymentInitiateResponse): void {
        // Create a form and submit to PayU
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = paymentResponse.payuBaseUrl;
        // Add all payment parameters to the form
        const fields = [
            { name: 'key', value: paymentResponse.key },
            { name: 'txnid', value: paymentResponse.txnid },
            { name: 'amount', value: paymentResponse.amount },
            { name: 'firstname', value: paymentResponse.firstname },
            { name: 'email', value: paymentResponse.email },
            { name: 'phone', value: paymentResponse.phone },
            { name: 'productinfo', value: paymentResponse.productinfo },
            { name: 'surl', value: paymentResponse.surl },
            { name: 'furl', value: paymentResponse.furl },
            { name: 'hash', value: paymentResponse.hash },
            { name: 'udf1', value: paymentResponse.udf1 },
            { name: 'udf2', value: paymentResponse.udf2 },
            { name: 'udf3', value: paymentResponse.udf3 },
            { name: 'udf4', value: paymentResponse.udf4 },
            { name: 'udf5', value: paymentResponse.udf5 }
        ];

        fields.forEach(field => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = field.name;
            input.value = field.value;
            form.appendChild(input);
        });

        // Append form to body and submit
        document.body.appendChild(form);
        form.submit();

        // Clean up
        document.body.removeChild(form);
    }

    formatAmount(amount: number): string {
        return amount.toFixed(2);
    }

    getProductInfo(planType: string): string {
        const productInfoMap: { [key: string]: string } = {
            'basic': 'TenderShark Basic Subscription',
            'standard': 'TenderShark Standard Subscription',
            'advance': 'TenderShark Advance Subscription',
            // Must stay distinguishable from 'advance': the API infers the plan
            // by substring-matching this string, and reads the 2-year validity
            // off the advancemax plan document.
            'advancemax': 'TenderShark Advance Max Subscription',
            'pro': 'TenderShark Pro Subscription',
            'tenderSharkX': 'TenderShark TenderSharkX Subscription'
        };
        return productInfoMap[planType] || 'TenderShark Subscription';
    }

    storePendingPaymentStatus(paymentData: PaymentStatusData): void {
        if (this.isBrowser()) {
            localStorage.setItem(this.PAYMENT_STATUS_STORAGE_KEY, JSON.stringify(paymentData));
        }
    }

    getPendingPaymentStatus(): PaymentStatusData | null {
        if (this.isBrowser()) {
            const data = localStorage.getItem(this.PAYMENT_STATUS_STORAGE_KEY);
            if (data) {
                localStorage.removeItem(this.PAYMENT_STATUS_STORAGE_KEY);
                try {
                    return JSON.parse(data);
                } catch (error) {
                    console.error('Error parsing payment status data:', error);
                    return null;
                }
            }
        }
        return null;
    }

    hasPendingPaymentStatus(): boolean {
        if (this.isBrowser()) {
            return !!localStorage.getItem(this.PAYMENT_STATUS_STORAGE_KEY);
        }
        return false;
    }

    clearPendingPaymentStatus(): void {
        if (this.isBrowser()) {
            localStorage.removeItem(this.PAYMENT_STATUS_STORAGE_KEY);
        }
    }
}
